// Cart routing: decide which store each list item is ordered from.
//
// Rules (agreed with Michael; refine together later):
//   0. An item only goes to stores that take its kind: regular Amazon gets pantry and
//      household items; Amazon Fresh and Costco get perishables (Costco takes both).
//   1. Cheapest first, comparing price per oz / fl oz / count when every priced store has
//      a size of the same kind, else by item price.
//   2. Ties (within 3%) go to the faster store, then the higher-priority store.
//   3. Items with no known price at any store go to the highest-priority store that carries them.
//   4. A store below its hard minimum is "holding": its items wait (they are cheaper there),
//      and the plan offers moving them to another store with the extra cost shown.
//   5. A store above its minimum but below its free-delivery threshold is still "ready";
//      the fee is shown.
//   6. Items with no store match are returned as `unmatched` so the UI can ask once.

import type { ListItem, Staple, StoreId, StoreProduct, StoreSettings } from './types.ts';
import { normalizeName, roundMoney } from './text.ts';
import { isPerishable, storesFor } from './perishable.ts';

export interface PlannedLine {
  item: ListItem;
  product: StoreProduct;
  /** Price per purchasable unit if known. */
  unitPrice?: number;
  /** qty * price if known. */
  lineTotal?: number;
}

export type StorePlanStatus = 'ready' | 'holding';

export interface MoveOption {
  storeId: StoreId;
  /** Extra estimated cost of moving every holding item there (can be 0 or negative). */
  extraCost: number;
  /** True when every holding item is available at that store. */
  complete: boolean;
}

export interface StorePlan {
  storeId: StoreId;
  storeName: string;
  status: StorePlanStatus;
  lines: PlannedLine[];
  /** Sum of known line totals. */
  subtotal: number;
  /** Lines without a known price (subtotal is then a lower bound). */
  unknownPriceCount: number;
  minimumOrder: number;
  /** How much more is needed to reach the hard minimum (0 when ready). */
  shortfall: number;
  /** Delivery fee that applies at this subtotal. */
  fee: number;
  freeDeliveryThreshold: number;
  deliveryHours: number;
  /** Plain-language reason for the status. */
  reason: string;
  /** For holding stores: where the items could go instead. */
  moveOptions: MoveOption[];
}

export interface CartPlan {
  generatedAt: string;
  stores: StorePlan[];
  /** Needed items that no enabled store carries yet. */
  unmatched: ListItem[];
}

export interface PlanInput {
  items: ListItem[];
  staples: Staple[];
  products: StoreProduct[];
  stores: StoreSettings[];
  now?: Date;
  /** Store ids forced by the user for specific items ("move these to Amazon"). */
  overrides?: Record<string, StoreId>;
}

const TIE_TOLERANCE = 0.03;

/** Find the staple an item refers to (by id, else by normalized name). */
export function stapleFor(item: ListItem, staples: Staple[]): Staple | undefined {
  if (item.stapleId) {
    const byId = staples.find((s) => s.id === item.stapleId);
    if (byId) return byId;
  }
  const key = normalizeName(item.name);
  return staples.find((s) => normalizeName(s.name) === key);
}

// Sizes in comparable base units: weight in oz, volume in fl oz, counts as-is.
const UNITS: Record<string, { dim: string; factor: number }> = {
  oz: { dim: 'weight', factor: 1 },
  lb: { dim: 'weight', factor: 16 },
  g: { dim: 'weight', factor: 0.035274 },
  kg: { dim: 'weight', factor: 35.274 },
  'fl oz': { dim: 'volume', factor: 1 },
  ml: { dim: 'volume', factor: 0.033814 },
  l: { dim: 'volume', factor: 33.814 },
  qt: { dim: 'volume', factor: 32 },
  gal: { dim: 'volume', factor: 128 },
  ct: { dim: 'count', factor: 1 },
};

/** Size in base units, or undefined when unknown. A size without a unit compares only with other unitless sizes. */
export function baseSize(p: Pick<StoreProduct, 'unitAmount' | 'unit'>): { dim: string; amount: number } | undefined {
  if (!p.unitAmount || p.unitAmount <= 0) return undefined;
  if (!p.unit) return { dim: 'unitless', amount: p.unitAmount };
  const u = UNITS[p.unit.trim().toLowerCase()];
  return u ? { dim: u.dim, amount: p.unitAmount * u.factor } : undefined;
}

/** Candidates for one item, best first. */
export function rankCandidates(
  item: ListItem,
  staples: Staple[],
  products: StoreProduct[],
  stores: StoreSettings[],
): StoreProduct[] {
  const staple = stapleFor(item, staples);
  if (!staple) return [];
  // Only stores that take this kind of item (regular Amazon: pantry and household only).
  const enabled = new Map(storesFor(stores.filter((s) => s.enabled), isPerishable(staple)).map((s) => [s.id, s]));
  const candidates = products.filter((p) => p.stapleId === staple.id && p.available && enabled.has(p.storeId));
  if (candidates.length === 0) return [];

  // Compare price per oz / fl oz / count when every priced candidate has a size of the same
  // kind (a 2 lb tub vs a 24 oz one); otherwise compare item prices.
  const priced = candidates.filter((p) => p.lastPrice != null);
  const sizes = new Map(priced.map((p) => [p, baseSize(p)]));
  const dims = new Set([...sizes.values()].map((s) => s?.dim));
  const useUnits = priced.length > 0 && dims.size === 1 && !dims.has(undefined);
  const priceOf = (p: StoreProduct) => (useUnits ? p.lastPrice! / sizes.get(p)!.amount : p.lastPrice);

  const bySpeedThenPriority = (a: StoreProduct, b: StoreProduct) => {
    const sa = enabled.get(a.storeId)!;
    const sb = enabled.get(b.storeId)!;
    if (sa.deliveryHours !== sb.deliveryHours) return sa.deliveryHours - sb.deliveryHours;
    return sa.priority - sb.priority;
  };
  const byPriorityThenSpeed = (a: StoreProduct, b: StoreProduct) => {
    const sa = enabled.get(a.storeId)!;
    const sb = enabled.get(b.storeId)!;
    if (sa.priority !== sb.priority) return sa.priority - sb.priority;
    return sa.deliveryHours - sb.deliveryHours;
  };

  // Priced candidates first, cheapest first (near-ties go to the faster store);
  // unpriced candidates after them in priority order (rule 3).
  const pricedSorted = [...priced].sort((a, b) => {
    const pa = priceOf(a)!;
    const pb = priceOf(b)!;
    const lo = Math.min(pa, pb);
    const tie = lo === 0 ? pa === pb : Math.abs(pa - pb) / lo <= TIE_TOLERANCE;
    return tie ? bySpeedThenPriority(a, b) : pa - pb;
  });
  const unpricedSorted = candidates.filter((p) => p.lastPrice == null).sort(byPriorityThenSpeed);
  return [...pricedSorted, ...unpricedSorted];
}

function feeFor(store: StoreSettings, subtotal: number): number {
  if (store.freeDeliveryThreshold <= 0) return 0;
  return subtotal >= store.freeDeliveryThreshold ? 0 : store.feeBelowThreshold;
}

function lineFor(item: ListItem, product: StoreProduct): PlannedLine {
  const unitPrice = product.lastPrice;
  return {
    item,
    product,
    unitPrice,
    lineTotal: unitPrice != null ? roundMoney(unitPrice * item.qty) : undefined,
  };
}

function summarize(store: StoreSettings, lines: PlannedLine[]): Omit<StorePlan, 'status' | 'reason' | 'moveOptions'> {
  const subtotal = roundMoney(lines.reduce((sum, l) => sum + (l.lineTotal ?? 0), 0));
  const unknownPriceCount = lines.filter((l) => l.lineTotal == null).length;
  const shortfall = Math.max(0, roundMoney(store.minimumOrder - subtotal));
  return {
    storeId: store.id,
    storeName: store.name,
    lines,
    subtotal,
    unknownPriceCount,
    minimumOrder: store.minimumOrder,
    shortfall,
    fee: feeFor(store, subtotal),
    freeDeliveryThreshold: store.freeDeliveryThreshold,
    deliveryHours: store.deliveryHours,
  };
}

export function planCarts(input: PlanInput): CartPlan {
  const now = input.now ?? new Date();
  const stores = input.stores.filter((s) => s.enabled);
  const storeById = new Map(stores.map((s) => [s.id, s]));
  const needed = input.items.filter((i) => i.status === 'needed');

  const assignments = new Map<StoreId, PlannedLine[]>();
  const ranked = new Map<string, StoreProduct[]>();
  const unmatched: ListItem[] = [];

  for (const item of needed) {
    const candidates = rankCandidates(item, input.staples, input.products, stores);
    ranked.set(item.id, candidates);
    if (candidates.length === 0) {
      unmatched.push(item);
      continue;
    }
    const forced = input.overrides?.[item.id] ?? item.storeOverride;
    const pick = (forced && candidates.find((c) => c.storeId === forced)) || candidates[0];
    const list = assignments.get(pick.storeId) ?? [];
    list.push(lineFor(item, pick));
    assignments.set(pick.storeId, list);
  }

  // Enforce per-store unique-item caps: overflow waits for the next order.
  const overflow = new Map<StoreId, PlannedLine[]>();
  for (const [storeId, lines] of assignments) {
    const cap = storeById.get(storeId)?.maxUniqueItems;
    if (cap && lines.length > cap) {
      overflow.set(storeId, lines.slice(cap));
      assignments.set(storeId, lines.slice(0, cap));
    }
  }

  const plans: StorePlan[] = [];
  for (const [storeId, lines] of assignments) {
    const store = storeById.get(storeId)!;
    const base = summarize(store, lines);
    // With unknown prices the subtotal is only a lower bound, so the minimum can't be judged:
    // don't hold items forever on missing data; let the store's own cart decide.
    const minimumUnknown = base.shortfall > 0 && base.unknownPriceCount > 0;
    if (base.shortfall === 0 || minimumUnknown) {
      const notes: string[] = [];
      if (store.minimumOrder === 0) notes.push('No minimum.');
      else if (minimumUnknown) notes.push(`Some prices aren't known yet; the store checks its $${store.minimumOrder} minimum at checkout.`);
      else notes.push(`Clears the $${store.minimumOrder} minimum.`);
      if (base.fee > 0) notes.push(`A $${base.fee.toFixed(2)} delivery fee applies below $${store.freeDeliveryThreshold}.`);
      const extra = overflow.get(storeId);
      if (extra) notes.push(`${extra.length} more items wait for the next order (store limit).`);
      plans.push({ ...base, status: 'ready', reason: notes.join(' '), moveOptions: [] });
    } else {
      plans.push({
        ...base,
        status: 'holding',
        reason: 'Cheapest here, so these wait until the cart clears the minimum.',
        moveOptions: moveOptionsFor(lines, storeId, ranked, stores),
      });
    }
  }

  plans.sort((a, b) => {
    if (a.status !== b.status) return a.status === 'ready' ? -1 : 1;
    return (storeById.get(a.storeId)!.priority ?? 0) - (storeById.get(b.storeId)!.priority ?? 0);
  });

  return { generatedAt: now.toISOString(), stores: plans, unmatched };
}

function moveOptionsFor(
  lines: PlannedLine[],
  fromStore: StoreId,
  ranked: Map<string, StoreProduct[]>,
  stores: StoreSettings[],
): MoveOption[] {
  const options: MoveOption[] = [];
  for (const target of stores) {
    if (target.id === fromStore) continue;
    let extra = 0;
    let complete = true;
    for (const line of lines) {
      const alt = ranked.get(line.item.id)?.find((p) => p.storeId === target.id);
      if (!alt) {
        complete = false;
        continue;
      }
      if (alt.lastPrice != null && line.product.lastPrice != null) {
        extra += (alt.lastPrice - line.product.lastPrice) * line.item.qty;
      }
    }
    const anyAvailable = lines.some((l) => ranked.get(l.item.id)?.some((p) => p.storeId === target.id));
    if (anyAvailable) options.push({ storeId: target.id, extraCost: roundMoney(extra), complete });
  }
  return options.sort((a, b) => Number(b.complete) - Number(a.complete) || a.extraCost - b.extraCost);
}

/** Short status line for the list screen and push notifications. */
export function describePlan(plan: CartPlan): string {
  const ready = plan.stores.filter((s) => s.status === 'ready');
  const holding = plan.stores.filter((s) => s.status === 'holding');
  if (ready.length === 0 && holding.length === 0) return 'Nothing to order yet.';
  const parts: string[] = [];
  for (const s of ready) parts.push(`${s.storeName} ready: ${s.lines.length} items, about $${Math.round(s.subtotal + s.fee)}`);
  for (const s of holding) parts.push(`${s.storeName} holding ${s.lines.length} items ($${Math.round(s.subtotal)} of $${s.minimumOrder})`);
  return parts.join(' · ');
}
